import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Medal, UsersThree, Certificate, Truck, ShieldCheck, Gauge,
  ArrowRight, ArrowUpRight, WhatsappLogo, Drop, Leaf, Quotes,
} from "@phosphor-icons/react";
import { IMG, PRODUCTS } from "@/lib/products";
import { openWhatsApp, yearsSince } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { SmartImage } from "@/components/SmartImage";
import { Reveal, StaggerGroup, StaggerItem, EASE } from "@/components/Reveal";
import { Counter } from "@/components/Counter";
import { Marquee } from "@/components/Marquee";
import { SectionHeading } from "@/components/PageBits";
import { ProductCard } from "@/components/ProductCard";
import { FounderSection } from "@/components/FounderSection";

const WHY = [
  { icon: Medal, key: "established" },
  { icon: UsersThree, key: "customers" },
  { icon: Certificate, key: "iso" },
  { icon: Truck, key: "panindia" },
  { icon: ShieldCheck, key: "quality" },
  { icon: Gauge, key: "performance" },
];

export default function Home() {
  const { t } = useLang();
  const years = yearsSince();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const heroLines = [t("hero.line1"), t("hero.line2")];
  const stats = [
    { value: years, suffix: "+", label: t("stats.years") },
    { value: 50000, suffix: "+", label: t("stats.customers") },
    { value: t("stats.panIndiaValue"), label: t("stats.supply") },
    { value: t("stats.isoValue"), label: t("stats.iso") },
    { value: 100, suffix: "%", label: t("stats.support") },
  ];
  const trust = [
    { icon: Medal, label: t("trust.established") },
    { icon: UsersThree, label: t("trust.customers") },
    { icon: Certificate, label: t("trust.iso") },
    { icon: Truck, label: t("trust.panIndia") },
  ];
  const manifesto = t("manifesto.items");
  const testimonials = t("testimonials.items");

  return (
    <div>
      {/* ---------------- HERO ---------------- */}
      <section
        ref={heroRef}
        className="relative flex min-h-screen items-center overflow-hidden bg-forest"
        data-testid="hero-section"
      >
        <motion.div style={{ scale: bgScale, y: bgY }} className="absolute inset-0">
          <SmartImage
            src={IMG.heroMain}
            alt="RK Jain industrial pumping and agricultural irrigation solutions"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <motion.div
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-r from-ink/85 via-ink/45 to-transparent"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-transparent to-ink/35" />
        <div className="hero-grain absolute inset-0" />

        <motion.div style={{ y: contentY }} className="container-x relative z-10 pt-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="overline mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" /> {t("hero.overline")}
          </motion.p>

          <h1 className="font-heading text-5xl font-extrabold leading-[1.02] tracking-tight text-white md:text-7xl lg:text-8xl">
            {heroLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.2 + i * 0.15 }}
                >
                  {i === 1 ? <span className="text-gold">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-white/80"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/products" className="btn-gold" data-testid="hero-explore-button">
              {t("common.exploreProducts")} <ArrowRight size={18} weight="bold" />
            </Link>
            <button
              onClick={() => openWhatsApp()}
              className="btn-outline-light"
              data-testid="hero-enquire-button"
            >
              <WhatsappLogo size={18} weight="fill" /> {t("common.enquireNow")}
            </button>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
          <div className="flex h-11 w-7 items-start justify-center rounded-full border border-white/40 p-1.5">
            <motion.span
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="h-2 w-1 rounded-full bg-gold"
            />
          </div>
        </div>
      </section>

      {/* ---------------- TRUST BADGES ---------------- */}
      <section className="bg-champagne" data-testid="trust-section">
        <div className="container-x grid grid-cols-2 divide-x divide-hairline border-y border-hairline lg:grid-cols-4">
          {trust.map((b, i) => (
            <Reveal key={i} delay={i * 0.08} className="flex items-center gap-4 px-5 py-8 md:px-8">
              <b.icon size={34} weight="thin" className="shrink-0 text-forest" />
              <span className="text-sm font-semibold leading-snug text-ink md:text-base">{b.label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- WHY RK JAIN ---------------- */}
      <section className="container-x py-24 md:py-32" data-testid="why-section">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <SectionHeading eyebrow={t("why.eyebrow")} title={t("why.title")} description={t("why.desc")} />
          <StaggerGroup className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((w) => (
              <StaggerItem key={w.key} className="rounded-2xl border border-hairline bg-surface p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-forest/20 hover:shadow-lg hover:shadow-forest/5">
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-champagne text-forest">
                  <w.icon size={30} weight="thin" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-forest">{t(`why.items.${w.key}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-subtle">{t(`why.items.${w.key}.text`)}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ---------------- STATISTICS ---------------- */}
      <section className="relative overflow-hidden bg-champagne py-24 md:py-28" data-testid="stats-section">
        <div className="container-x relative">
          <StaggerGroup className="grid grid-cols-2 gap-y-12 md:grid-cols-3 lg:grid-cols-5">
            {stats.map((s, i) => (
              <StaggerItem key={i} className="border-l-2 border-gold pl-5">
                <div className="font-heading text-4xl font-extrabold tracking-tight text-forest md:text-5xl">
                  <Counter value={s.value} suffix={s.suffix || ""} />
                </div>
                <p className="mt-3 text-sm font-medium text-subtle">{s.label}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ---------------- PRODUCTS PREVIEW ---------------- */}
      <section className="container-x py-24 md:py-32" data-testid="products-preview">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading eyebrow={t("productsSection.eyebrow")} title={t("productsSection.title")} description={t("productsSection.desc")} />
          <Link to="/products" className="btn-outline-forest shrink-0" data-testid="view-all-products">
            {t("common.viewAllProducts")} <ArrowUpRight size={18} weight="bold" />
          </Link>
        </div>
        <div className="mt-14 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.slice(0, 8).map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.08}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <Marquee items={t("marquee")} />

      {/* ---------------- ISO CERTIFICATION ---------------- */}
      <section className="bg-champagne py-24 md:py-32" data-testid="iso-section">
        <div className="container-x grid items-center gap-14 lg:grid-cols-2">
          <Reveal className="order-2 lg:order-1">
            <p className="overline mb-4">{t("iso.eyebrow")}</p>
            <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-forest md:text-5xl">
              {t("iso.title")}
            </h2>
            <p className="mt-6 max-w-lg text-lg text-ink/75">{t("iso.desc")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {t("iso.chips").map((c) => (
                <span key={c} className="rounded-lg border border-hairline bg-surface px-4 py-2 text-sm font-medium text-forest shadow-sm">
                  {c}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="order-1 flex justify-center lg:order-2">
            <div className="relative flex h-72 w-72 items-center justify-center rounded-full bg-forest md:h-80 md:w-80">
              <span className="absolute inset-4 animate-sun-spin rounded-full border border-dashed border-gold/40" />
              <div className="flex h-44 w-44 flex-col items-center justify-center rounded-full border-2 border-gold text-center md:h-52 md:w-52">
                <Certificate size={48} weight="thin" className="text-gold" />
                <span className="mt-2 font-heading text-3xl font-extrabold text-white">ISO</span>
                <span className="text-xs uppercase tracking-[0.2em] text-white/70">{t("iso.certified")}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- MANIFESTO ---------------- */}
      <section className="container-x py-24 md:py-32" data-testid="manifesto-section">
        <SectionHeading eyebrow={t("manifesto.eyebrow")} title={t("manifesto.title")} />
        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
          {manifesto.map((m, i) => (
            <Reveal key={i} delay={i * 0.1} className="rounded-2xl border border-hairline bg-surface p-8 shadow-sm md:p-10">
              <span className="font-serif text-5xl italic text-gold">{`0${i + 1}`}</span>
              <h3 className="mt-6 font-heading text-2xl font-bold text-forest">{m.title}</h3>
              <p className="mt-4 leading-relaxed text-subtle">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- FOUNDER ---------------- */}
      <FounderSection />

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="bg-surface py-24 md:py-32" data-testid="testimonials-section">
        <div className="container-x">
          <SectionHeading eyebrow={t("testimonials.eyebrow")} title={t("testimonials.title")} />
          <StaggerGroup className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {testimonials.map((tm, i) => (
              <StaggerItem key={i} className="flex flex-col rounded-2xl border border-hairline bg-sand p-8 shadow-sm">
                <Quotes size={40} weight="fill" className="text-gold" />
                <p className="mt-5 flex-1 text-ink/80">&ldquo;{tm.text}&rdquo;</p>
                <div className="mt-8 border-t border-hairline pt-5">
                  <p className="font-heading font-bold text-forest">{tm.name}</p>
                  <p className="text-sm text-subtle">{tm.role}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="bg-sand py-20 md:py-28" data-testid="cta-section">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl bg-forest px-8 py-14 shadow-xl shadow-forest/15 md:px-16 md:py-20">
            <div className="hero-grain absolute inset-0 opacity-40" />
            <div className="relative flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
              <div className="max-w-2xl">
                <div className="mb-4 flex items-center gap-3 text-gold">
                  <Leaf size={22} weight="fill" /> <Drop size={22} weight="fill" />
                </div>
                <h2 className="font-heading text-3xl font-extrabold leading-tight tracking-tight text-sand md:text-5xl">
                  {t("cta.title")}
                </h2>
                <p className="mt-4 text-lg text-sand/80">{t("cta.desc")}</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => openWhatsApp()}
                  className="inline-flex items-center gap-2 rounded-xl bg-gold px-8 py-4 font-semibold text-forest transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20"
                  data-testid="cta-whatsapp-button"
                >
                  <WhatsappLogo size={20} weight="fill" /> {t("cta.whatsapp")}
                </button>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-xl border border-sand/40 px-8 py-4 font-semibold text-sand transition-colors hover:bg-sand hover:text-forest"
                  data-testid="cta-contact-button"
                >
                  {t("cta.contact")} <ArrowRight size={18} weight="bold" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
